import { AxiosError, AxiosRequestConfig } from 'axios';

import { store } from 'store/store';
import { LocalStore } from 'constants/localstore.ts';
import { saveToken } from 'components/auth/auth.helper.tsx';
import { setIsAuthenticated } from 'components/app/_mediator/app.slice';
import { reLogin } from './auth.service.ts';
import { api } from './_api-client.ts';

interface FailedRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshingToken = false;
let refreshQueue: FailedRequest[] = [];

export const handleRefreshToken = async (error: AxiosError): Promise<unknown> => {
  const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

  if (error.response?.status !== 401 || originalRequest._retry) {
    return Promise.reject(error);
  }

  const refreshToken = localStorage.getItem(LocalStore.refreshToken);
  if (!refreshToken) return logout(error);

  originalRequest._retry = true;

  if (isRefreshingToken) {
    return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject }))
      .then(() => retryOriginalRequest(originalRequest))
      .catch(() => logout(error));
  }

  isRefreshingToken = true;

  try {
    const success = await performRefresh(refreshToken);
    return success ? retryOriginalRequest(originalRequest) : logout(error);
  } catch (err) {
    processQueue(err);
    return logout(error);
  } finally {
    isRefreshingToken = false;
  }
};

/* H E L P E R S */

const processQueue = (error: unknown) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

const logout = (error: unknown): Promise<never> => {
  store.dispatch(setIsAuthenticated(false));
  return Promise.reject(error);
};

const retryOriginalRequest = async (request: AxiosRequestConfig): Promise<unknown> => {
  return api(request);
};

const performRefresh = async (refreshToken: string) => {
  const result = await reLogin(refreshToken);
  if (result.data.executionResult) {
    saveToken(result.data);
    processQueue(null);
    return true;
  }
  return false;
};
